import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RetailerAttributeDefinitionsService } from '@src/app/shared/backend/clients';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { ConfirmationService } from 'primeng/api';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-view-retailer-attribute-definitions',
    templateUrl: './view-retailer-attribute-definitions.component.html',
    styleUrl: './view-retailer-attribute-definitions.component.scss',
    host: {
        class: 'app-view-retailer-attribute-definitions'
    },
    providers: [ConfirmationService],
    standalone: false
})
export class ViewRetailerAttributeDefinitionsComponent extends BaseViewComponent implements OnInit, OnDestroy {
  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  allowModifyRetailerAttribute = false;
  retailerAttributeDefinitionId = 0;
  stateOptions: any[];
  stateInitialValue: any;
  stateNewValue: any;
  sanitizedConfirmMessage: SafeHtml | undefined;
  retailerAttributesDefinition: any = null;

  constructor(
    private permissionService: PermissionService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private confirmationService: ConfirmationService,
    private retailerAttributeDefinitionsService: RetailerAttributeDefinitionsService,
  ) {
    super();
    this.updateStateOptions();
  }

  ngOnInit() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(() => {
        this.updateStateOptions();
      });

    this.init();
  }

  private async init(): Promise<void> {
    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(params => {
        this.retailerAttributeDefinitionId = Number(params['retailerAttributeDefinitionId']);
        if (this.retailerAttributeDefinitionId > 0) {
          this.get();
        }
      });

    this.allowModifyRetailerAttribute = await this.permissionService.hasActionPermissions(
      Actions.modify_productattributedefinitions
    );
  }

  private updateStateOptions(): void {
    this.stateOptions = [
      {
        label: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        value: true
      },
      {
        label: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        value: false
      }
    ];
  }

  initStateSwitch() {
    this.stateInitialValue = this.retailerAttributesDefinition.isActive;

    const selectedLabel = this.stateOptions.find(option => option.value === this.stateInitialValue)?.label.toLowerCase();
    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);
  }

  onStateChange(event: any) {
    this.stateNewValue = event.value;
    const selectedLabel = this.stateOptions.find(option => option.value === this.stateNewValue)?.label.toLowerCase();

    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);

    // State update confirmation dialog
    this.confirmationService.confirm({
      key: 'confirm-state-change-dialog',
      header: this.translate.instant('MODALBOXES.TITLE_CONFIRM_STATE_CHANGE')
    });
  }

  override async get() {
    try {
      this.retailerAttributesDefinition = await firstValueFrom(
        this.retailerAttributeDefinitionsService.apiRetailerAttributeDefinitionsDetailsIdGet(
          this.retailerAttributeDefinitionId
        )
      );
      this.initStateSwitch();
    } catch (error) {
      console.log('Error fetching retailer attribute definition data:', error);
    }
  }

  goToEditRetailerAttributesDefinitions() {
    this.router.navigate([
      `/retailer-attribute-definitions/${this.retailerAttributeDefinitionId}/edit`
    ]);
  }

    onConfirmStateChange(): void {
    if (this.stateNewValue === false) {
      this.retailerAttributeDefinitionsService.apiRetailerAttributeDefinitionsIdDeactivatePatch(this.retailerAttributeDefinitionId).subscribe({
        next: async () => {
          await this.get();
        },
        error: (error) => {
          console.error('Failed to deactivate retailer', error);
        }
      });
    }
    if (this.stateNewValue === true) {
      this.retailerAttributeDefinitionsService.apiRetailerAttributeDefinitionsIdActivatePatch(this.retailerAttributeDefinitionId).subscribe({
        next: async () => {
          await this.get();
        },
        error: (error) => {
          console.error('Failed to activate retailer', error);
        }
      });
    }
    this.confirmationService.close();
  }

  onCancelStateChange() {
    // Revert state change
    const nonMatchingOption = this.stateOptions.find(option => option.value !== this.stateInitialValue).value;
    this.stateInitialValue = nonMatchingOption;
    this.confirmationService.close();
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
